import { Injectable, NgZone, OnDestroy, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { EntitlementLevelEnum, EntitlementsService, FeatureMappingEnum } from '@unified-ui/entitlements';
import { decodeJWT } from 'aws-amplify/auth';
import { Confirmation, ConfirmationService } from 'primeng/api';
import { fromEvent } from 'rxjs';
import { authSelectAuthLastAction, authSelectAuthenticated } from '../auth-store';
import { authSetLastAction } from '../auth-store/auth.actions';
import { AuthTokensService } from '../auth-tokens.service';
import { AuthService } from '../auth.service';
import { TOKEN_TYPE } from '../types/tokens.type';

@Injectable({
    providedIn: 'root'
})
export class AutoLogoutService implements OnDestroy {
    private _authService = inject(AuthService);
    private _authTokensService = inject(AuthTokensService);
    private _authStore = inject(Store);
    private _router = inject(Router);
    private _ngZone = inject(NgZone);
    private _confirmationService = inject(ConfirmationService);
    private _entitlementsService = inject(EntitlementsService);

    private readonly CHECK_INTERVAL = 5000; // in ms
    private MINUTES_UNTIL_AUTO_LOGOUT = 30; // default in Minutes
    private _warningIntervalId: ReturnType<typeof setInterval> | undefined = undefined;
    private _countDown = 0;

    private _isAuthenticated = false;
    private _lastActionTime = Date.now();

    private _authenticated$ = this._authStore.select(authSelectAuthenticated).subscribe({
        next: (v: boolean) => {
            this._isAuthenticated = v;
        },
        error: (err: Error) => {
            console.error(err);
        }
    });

    private _lastActionTime$ = this._authStore.select(authSelectAuthLastAction).subscribe({
        next: (v: number) => {
            this._lastActionTime = v;
        },
        error: (err: Error) => {
            console.error(err);
        }
    });

    private _confirmation: Confirmation = {
        header: 'Your session is about to expire',
        message: '',
        key: 'confirmDialog',
        icon: 'pi pi-exclamation-circle',
        acceptIcon: 'null',
        rejectIcon: 'null',
        acceptLabel: 'Stay logged in',
        rejectLabel: 'Log out',
        rejectButtonStyleClass: 'p-button-secondary p-button-text',
        accept: () => {
            this._clearWarningInterval();
            this.reset();
        },
        reject: () => {
            this._clearWarningInterval();
            this._logout();
        }
    };

    constructor() {
        this._initListener();
        this._initInterval();

        // Sets the expiration time
        effect(() => {
            const accessToken = this._authTokensService.getAuthToken(TOKEN_TYPE.ACCESS);
            if (this._authTokensService.tokensReady$() && accessToken) {
                const decodedToken = decodeJWT(accessToken ?? '');
                const expirationTimeout = decodedToken.payload.exp;
                this.MINUTES_UNTIL_AUTO_LOGOUT = expirationTimeout ? this._getMinutesToLogout(expirationTimeout) : this.MINUTES_UNTIL_AUTO_LOGOUT;
            }
        });
    }

    public ngOnDestroy(): void {
        this._authenticated$.unsubscribe();
        this._lastActionTime$.unsubscribe();
        this._clearWarningInterval();
    }

    // Reset the time of the last action
    public reset(time: number = Date.now()) {
        this._authStore.dispatch(authSetLastAction({ lastActionTime: time }));
    }

    /**
     * @description Sets listener into the page to capture user clicks.
     * Resets the timer when a click is detected
     */
    private _initListener(): void {
        this._ngZone.runOutsideAngular(() => {
            fromEvent(document.body, 'click').subscribe(() => this.reset());
        });
    }

    /**
     * @description Inits interval to check for expiration
     */
    private _initInterval() {
        this._ngZone.runOutsideAngular(() => {
            setInterval(() => {
                this._checkLastAction();
            }, this.CHECK_INTERVAL);
        });
    }

    /**
     * @description Checks last action time and emits a logout warning if <=20s exist before timeout
     */
    private _checkLastAction(): void {
        // Skip the check if they haven't been authenticated yet.
        if (this._isAuthenticated === false) {
            return;
        }

        const timeleft = this._lastActionTime + this.MINUTES_UNTIL_AUTO_LOGOUT * 60 * 1000;
        const diff = timeleft - Date.now();
        const isTimeout = diff < 0;

        // Give user a 30 second warning
        if (diff <= 30_000 && this._isAuthenticated && !this._warningIntervalId) {
            this._countDown = Math.round(diff / 1000);
            this._ngZone.run(() => this._warningTimer());
        }

        this._ngZone.run(() => {
            if (isTimeout && this._isAuthenticated) {
                this._logout();
            }
        });
    }

    private _logout(): void {
        this._clearWarningInterval();
        this._confirmationService.close();
        this._authService.logOut();
        this._router.navigate(['login']);
    }

    private _warningTimer = () => {
        // Check if user has PLANNING entitlement
        const hasGLPEntitlement = this._entitlementsService.hasEntitlement(FeatureMappingEnum.PLANNING, EntitlementLevelEnum.FEATURE);

        // Set appropriate message based on entitlements
        if (hasGLPEntitlement) {
            this._confirmation.message = `You will be automatically logged out of Guideline Planning & Advanced Analytics in ${this._countDown} seconds.`;
        } else {
            this._confirmation.message = `You will be automatically logged out in ${this._countDown} seconds.`;
        }

        this._confirmationService.confirm(this._confirmation);

        this._warningIntervalId = setInterval(() => {
            this._countDown = this._countDown - 1;

            // Update message with current countdown
            if (hasGLPEntitlement) {
                this._confirmation.message = `You will be automatically logged out of Guideline Planning & Advanced Analytics in ${this._countDown} seconds.`;
            } else {
                this._confirmation.message = `You will be automatically logged out in ${this._countDown} seconds.`;
            }

            this._confirmationService.confirm(this._confirmation);
            if (this._countDown <= 0) this._clearWarningInterval();
        }, 1000);
    };

    private _clearWarningInterval(): void {
        if (this._warningIntervalId) clearInterval(this._warningIntervalId);
        this._warningIntervalId = undefined;
    }

    // Get logout time in minutes
    private _getMinutesToLogout(exp: number): number {
        return (exp - Math.floor(Date.now() / 1000)) / 60;
    }
}
